import { Injectable } from '@angular/core';

import { tap } from 'rxjs';
import { TsSnackbarService } from 'tsui';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { Properties } from './actions';
import { StateBase } from '@state/state-base';
import { GraphqlResultPageInfo } from '@shared/models';
import GraphqlService from '@services/graphql.service';
import { generateDownloadableFile } from '@shared/functions';

export interface PropertiesStateModel {
  graphqlResults: {
    rows: any[];
    pageInfo?: GraphqlResultPageInfo;
    timeSeries?: {
      columns: string[];
      isGrouped?: boolean;
    };
  };
}

@State<PropertiesStateModel>({
  name: new StateToken<PropertiesStateModel>('propertiesState'),
  defaults: { graphqlResults: null },
})
@Injectable()
export class PropertiesState extends StateBase {
  constructor(
    private readonly graphqlService: GraphqlService,
    private readonly snackBarService: TsSnackbarService,
  ) {
    super();
  }

  @Selector()
  static graphqlResults(state: PropertiesStateModel) {
    return state.graphqlResults;
  }

  @Action(Properties.Graphql.Action)
  graphql(
    context: StateContext<PropertiesStateModel>,
    { graphqlParams }: { graphqlParams: Properties.Graphql.Params },
  ) {
    const filter = { ...graphqlParams.filter };

    return this.graphqlService
      .query(
        {
          connection: graphqlParams.connection,
          fieldDefs: graphqlParams.fieldDefs || [],
          fields: [...new Set(['id', 'name', ...(graphqlParams.fields ?? [])]).values()],
          filter,
          options: graphqlParams.options,
        },
        graphqlParams.exportOptions,
      )
      .pipe(
        tap((result: any) => {
          if (graphqlParams.exportOptions && result.message) {
            this.snackBarService.open(result.message);
          } else if (graphqlParams.exportOptions) {
            generateDownloadableFile(
              result as any,
              graphqlParams.exportOptions.format,
              graphqlParams.exportOptions.fileName,
            );
          } else {
            const { edges, pageInfo } = result;
            const state = context.getState();

            context.setState({
              ...state,
              graphqlResults: {
                rows: edges.map(({ node }) => node),
                pageInfo,
              },
            });
          }
        }),
      );
  }
}
